import { ATTRIBUTION_TAB_IDS, REQUEST_TYPES, STANDARD_TRACKING_LINK_PARAMETERS, TRANSLATE_PREFIX } from './consts';
import { PRIMARY_TRACKING_LINK_KEYS } from './validators';

const POSTBACK_TAB_IDS = new Set([ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP, ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES]);

const hasDuplicateKeys = (params, keyName) => {
    const seen = new Set();
    return params.some(p => {
        if (!p[keyName]) return false;
        if (seen.has(p[keyName])) return true;
        seen.add(p[keyName]);
        return false;
    });
};

const ATTRIBUTION_RULES = {
    isDraftBlocked: formData => {
        const general = formData[ATTRIBUTION_TAB_IDS.GENERAL] || {};
        if (!general.platform_template) return true;
        const params = formData[ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS]?.tracking_link_parameters || [];
        return (
            hasDuplicateKeys(params, 'singular_key') || params.some(p => p.partner_macro && /\s/.test(p.partner_macro))
        );
    },

    getDraftBlockedReasons: formData => {
        const reasons = [];
        const general = formData[ATTRIBUTION_TAB_IDS.GENERAL] || {};
        if (!general.platform_template) {
            reasons.push({
                tabId: ATTRIBUTION_TAB_IDS.GENERAL,
                reasonKey: `${TRANSLATE_PREFIX}.ERRORS.PLATFORM_TEMPLATE_REQUIRED`,
            });
        }
        const params = formData[ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS]?.tracking_link_parameters || [];
        if (hasDuplicateKeys(params, 'singular_key')) {
            reasons.push({
                tabId: ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS,
                reasonKey: `${TRANSLATE_PREFIX}.ERRORS.TRACKING_LINK_PARAM_DUPLICATE_KEY`,
            });
        }
        if (params.some(p => p.partner_macro && /\s/.test(p.partner_macro))) {
            reasons.push({
                tabId: ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS,
                reasonKey: `${TRANSLATE_PREFIX}.ERRORS.TRACKING_LINK_PARAM_MACRO_NO_SPACES`,
            });
        }
        return reasons;
    },

    isTabVisible: (formData, tab) => {
        if (!POSTBACK_TAB_IDS.has(tab.id)) return true;
        const general = formData[ATTRIBUTION_TAB_IDS.GENERAL] || {};
        return general.receive_postbacks ?? true;
    },

    prepareFormData: formData => {
        // When loading an existing submitted request, primary tracking link params may have been
        // stripped by filterFormData on submit. Prepend any that are missing so they appear in the
        // table for editing. Existing saved params (including their order) are left untouched.
        const trackingTab = ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS;
        const savedParams = formData[trackingTab]?.tracking_link_parameters || [];
        const savedPrimaryKeys = new Set(
            savedParams.filter(p => PRIMARY_TRACKING_LINK_KEYS.has(p.singular_key)).map(p => p.singular_key)
        );
        const missingPrimaryParams = STANDARD_TRACKING_LINK_PARAMETERS.filter(
            p => p.primary && !savedPrimaryKeys.has(p.singularKey)
        ).map(({ singularKey, fieldName }) => ({
            singular_key: singularKey,
            field_name: fieldName,
            partner_macro: undefined,
        }));
        if (missingPrimaryParams.length === 0) return formData;
        return {
            ...formData,
            [trackingTab]: {
                ...formData[trackingTab],
                tracking_link_parameters: [...missingPrimaryParams, ...savedParams],
            },
        };
    },

    filterFormData: formData => {
        const trackingTab = ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS;
        return {
            ...formData,
            [trackingTab]: {
                ...formData[trackingTab],
                tracking_link_parameters: (formData[trackingTab]?.tracking_link_parameters || []).filter(
                    p => !PRIMARY_TRACKING_LINK_KEYS.has(p.singular_key) || !!p.partner_macro
                ),
            },
        };
    },

    isTabDisabled: (formData, _tab, index) => {
        const general = formData[ATTRIBUTION_TAB_IDS.GENERAL] || {};
        return !general.platform_template && index > 0;
    },

    tabDisabledTooltipKey: `${TRANSLATE_PREFIX}.TOOLTIPS.TAB_DISABLED`,
};

const DEFAULT_RULES = {
    isDraftBlocked: () => false,
    getDraftBlockedReasons: () => [],
    prepareFormData: formData => formData,
    filterFormData: formData => formData,
    isTabVisible: () => true,
    isTabDisabled: () => false,
    tabDisabledTooltipKey: undefined,
};

const FORM_TYPE_RULES = {
    [REQUEST_TYPES.ATTRIBUTION]: ATTRIBUTION_RULES,
};

export const getFormTypeRules = type => ({ ...DEFAULT_RULES, ...FORM_TYPE_RULES[type] });
